import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";
import { formatDate } from "@/utils/content";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Link } from "react-router-dom";

const PAGE_SIZE = 4;
type SortKey = "latest" | "hot";

const SORTS: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新优先", icon: "ri-time-line" },
  { key: "hot", label: "最热优先", icon: "ri-fire-line" },
];

export default function NewsCategory() {
  useDocumentTitle({
    title: "影视资讯 - 我妻子的一切最新动态",
    description:
      "《我妻子的一切》影视资讯专区，汇总剧集收视动态、幕后花絮、主创专访与官方公告，第一时间了解剧集相关内容。",
    keywords: "我妻子的一切资讯,影视资讯,剧集动态,幕后花絮",
  });

  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const list = [...news];
    if (sort === "hot") {
      list.sort((a, b) => b.hot - a.hot);
    } else {
      list.sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return list;
  }, [sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "影视资讯" }]} />

      <header className="mt-5 max-w-3xl">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Latest News</p>
        <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          影视资讯
        </h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
          汇总《我妻子的一切》的剧集动态与周边资讯，包括收视表现、幕后制作、主创访谈以及官方发布的各类公告。
        </p>
      </header>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <p className="text-sm text-foreground-500">
          共 <span className="font-medium text-primary-700">{sorted.length}</span> 条资讯
        </p>
        <div className="flex items-center gap-1 rounded-full border border-background-300 p-1">
          {SORTS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                setSort(item.key);
                setPage(1);
              }}
              className={[
                "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap cursor-pointer md:text-sm",
                sort === item.key
                  ? "bg-primary-700 text-background-50"
                  : "text-foreground-600 hover:text-primary-700",
              ].join(" ")}
            >
              <i className={item.icon} />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 flex flex-col gap-4">
            {pageItems.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:flex-row md:p-5"
              >
                <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 md:h-32 md:w-52">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 我妻子的一切资讯`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
                    <span className="rounded bg-secondary-200 px-1.5 py-0.5 text-secondary-900">资讯</span>
                    <span>{formatDate(item.date)}</span>
                    <span className="inline-flex items-center gap-1 text-accent-700">
                      <i className="ri-fire-line" />
                      {item.hot}
                    </span>
                    <span className="hidden sm:inline">· {item.source}</span>
                  </div>
                  <h2 className="mt-2 line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                    {item.title}
                  </h2>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-6 text-foreground-500">
                    {item.summary}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-background-100 px-2 py-0.5 text-xs text-foreground-500"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-5">
          <EmptyState icon="ri-newspaper-line" title="暂无相关资讯" description="暂时没有可展示的资讯内容。" />
        </div>
      )}
    </div>
  );
}